import config from './config';
import { createTransferState } from './transferChallenge';
import { createSignupState } from './signupChallenge';
import { signupTransport } from './signupTransport';
import globals from './globals';
import utils from './utils';
import logger from './logger';
// import $ from '../assets/scripts/jquery-3.6.0.min';
import moment from '../assets/scripts/moment.min';
import * as msalAuth from './msal/auth';
import * as gisAuth from './gis/auth';

const $ = window.$;
var TScMessenger = window.TScMessenger;

TScMessenger.writeDebugMessage("Security.js");
var
    securityTokenValue = '',
    validUntilValue = '',
    machineCodeValue = '',
    cdgValue = '',
    userNameProvided,
    passwordProvided,
    machineCodeProvided,
    cdgProvided,
    passwordObject = undefined,
    isFromSignUpProvided,
    dataManagerLogCallback,
    authenticationMethodToUse,
    localStorageKey = '';

// A request owns a credential snapshot. Full login, password continuation and
// logout invalidate earlier work; ordinary response re-protection does not.
let incarnation = 0, credentialRevision = 0, authorityRevision = 0, userContextRevision = 0, renewal = null, signingOut = false, credentialDenied = false;
let renewalPromise = null;
let recoveryFailureSequence = 0;
let lastRenewalDenial = null;
let protocolMode = localStorage.getItem('tsSessionProtocol') || 'unknown';
if (config.legacySessionConversion === true) localStorage.setItem('tsLegacyConversion', 'selected');
let credentialContext = null;
const pendingRequests = new Set();
const cancellationHandlers = new Set();
const signOutMarker = 'tsExplicitSignOut';
const transferState = createTransferState(sessionStorage, () => window.crypto.randomUUID());
const signupState = createSignupState(sessionStorage);
let publicSignupRequest = null;
let cancelStalePublicSignup = null;
const ordinaryHeaders = ['SecurityToken', 'ValidUntil', 'LocalStorageKey', 'MachineCode'];
function pendingPurpose(data, xhr) {
    const hasSignup = data?.PendingSignup != null, hasTransfer = data?.PendingTransfer != null;
    if (data?.Result?.ReturnCode !== 3 && !hasSignup && !hasTransfer) return null;
    const policyFailure = data?.Result?.ReturnCode === -7 && hasSignup && data.PendingSignup.State === 'password-policy';
    if ((data?.Result?.ReturnCode !== 3 && !policyFailure) || data.User != null || hasSignup === hasTransfer ||
        ordinaryHeaders.some(h => xhr.getResponseHeader(h))) throw new Error('Invalid restricted response');
    return hasSignup ? 'signup' : 'transfer';
}

function captureProof() {
    return { incarnation, revision: credentialRevision, authority: authorityRevision, token: securityTokenValue };
}
function sameAuthority(proof) {
    return proof && proof.incarnation === incarnation && proof.authority === authorityRevision && !signingOut;
}
// Admit only the denial that revoked this proof, never a late body or headers.
function isCurrentRenewalDenial(proof, error) {
    return !!(proof && error?.authenticationFailed && lastRenewalDenial && !signingOut && credentialDenied &&
        proof.incarnation === incarnation && error.recoveryOwner === incarnation &&
        error.recoveryFailure === lastRenewalDenial.failure && proof.authority === lastRenewalDenial.authority &&
        authorityRevision === lastRenewalDenial.authority + 1);
}
function isCurrent(proof) {
    return sameAuthority(proof) && proof.revision === credentialRevision;
}
function authenticationError(xhr = {}, textStatus) {
    const status = xhr.status || 0;
    const wasCancelled = textStatus === 'abort' || textStatus === 'canceled';
    const serviceFailure = status >= 500 && status <= 599;
    const retry = xhr.getResponseHeader?.('Retry-After');
    const retryAfterSeconds = typeof retry === 'string' && /^\d{1,5}$/.test(retry) ? Math.min(3600, Math.max(1, Number(retry))) : null;
    return { status, recoveryOwner: incarnation, recoveryFailure: ++recoveryFailureSequence,
        cancelled: wasCancelled, timeout: textStatus === 'timeout', retryable: !wasCancelled && (status === 0 || status === 429 || serviceFailure),
        throttled: status === 429, retryAfterSeconds: status === 429 ? (retryAfterSeconds || 60) : null,
        infrastructureUnavailable: serviceFailure, transportUnavailable: status === 0 && !wasCancelled, offline: status === 0 && !wasCancelled && navigator.onLine === false,
        authenticationFailed: status === 401, permissionDenied: status === 403,
        errorMessage: textStatus === 'timeout' ? 'Request timed out' : (xhr.statusText || 'Authentication required') };
}
function cancelled() { return { status: 0, cancelled: true, errorMessage: 'Session changed' }; }
function guardedPromise(promise, owner, proof = () => null) {
    const current = () => owner === incarnation && !signingOut && (!proof() || sameAuthority(proof()));
    // Re-evaluate admission on subscription, including an already fulfilled
    // result. Return an actual Deferred to jQuery aggregates: suppressing done
    // callbacks leaves $.when and always cleanup pending forever.
    function admitted() {
        const result = $.Deferred();
        promise.done((...args) => current() ? result.resolve(...args) : result.reject(cancelled()));
        promise.fail((...args) => result.reject(...(current() ? args : [cancelled()])));
        promise.progress((...args) => { if (current()) result.notify(...args); });
        return result.promise();
    }
    const wrapped = {
        state: () => admitted().state(),
        promise: () => admitted(),
        then(done, fail) {
            const next = admitted().then(done, fail);
            return guardedPromise(next, owner, proof);
        },
        done(...callbacks) { admitted().done(...callbacks); return wrapped; },
        progress(...callbacks) { admitted().progress(...callbacks); return wrapped; },
        fail(...callbacks) { admitted().fail(...callbacks); return wrapped; },
        always(...callbacks) { admitted().always(...callbacks); return wrapped; }
    };
    return wrapped;
}
function invalidatePending() {
    incarnation++;
    credentialRevision++;
    window.TruckScienceLaunch?.clear();
    for (const cancel of cancellationHandlers) cancel();
    cancellationHandlers.clear();
    renewal = null;
    globals.loginDfd = null;
    globals.dmLoginDfd = null;
    for (const request of pendingRequests) request.abort();
    pendingRequests.clear();
}
function rememberProtocol(token) {
    // This is advertised by a successful credential response, never a failed
    // refresh. Unknown marked formats must not enter the legacy path.
    protocolMode = token.startsWith('tsc1.') ? 'cookie-v1' : (token.includes('.') ? 'unknown' : 'legacy');
    localStorage.setItem('tsSessionProtocol', protocolMode);
}
function adoptResponse(xhr, proof, passwordChange = false) {
    if (!isCurrent(proof)) return false;
    const token = xhr.getResponseHeader('SecurityToken');
    const deadline = xhr.getResponseHeader('ValidUntil');
    if (!token || !deadline) return true; // Unauthenticated translations have no credential response.
    if (protocolMode === 'cookie-v1' && !token.startsWith('tsc1.')) return false;
    securityTokenValue = token;
    validUntilValue = deadline;
    rememberProtocol(token);
    if (passwordChange) { credentialRevision++; authorityRevision++; }
    return true;
}

function ensureFresh(requireContext = false) {
    const dfd = $.Deferred();
    dfd.fail(error => { if (error && !error.cancelled) { error.renewalFailure = true; error.protectedDispatched = false; } });
    if (signingOut || credentialDenied || localStorage.getItem(signOutMarker)) return dfd.reject(authenticationError({ status: 401 })).promise();
    // Legacy context requires the full cookie-authenticated response even when
    // the current access token is fresh. It cannot use marked-only context.
    if (securityTokenIsValid().IsValid && !(requireContext && protocolMode === 'legacy')) return dfd.resolve(captureProof()).promise();
    if (transferState.resume() || signupState.resume()) return dfd.reject({ status: 401, pendingChallenge: true }).promise();
    if (renewal) return renewalPromise;
    if (protocolMode === 'unknown' && securityTokenValue && securityTokenValue.includes('.')) return dfd.reject(authenticationError({ status: 401 })).promise();
    renewal = dfd;
    renewalPromise = guardedPromise(dfd.promise(), incarnation);
    const proof = captureProof();
    const finish = () => { if (renewal === dfd) renewal = null; };
    const dispatch = phaseReady => {
        if (!phaseReady) {
            const method = globals.user.getAuthenticationMethod();
            const provider = method === config.AUTHENTICATION_METHOD.GOOGLE || method === config.AUTHENTICATION_METHOD.MICROSOFT;
            login(provider ? globals.user.getEmailAddress() : '', provider ? globals.user.getThirdPartyLoggedInUserTokenAsPassword() : '',
            '', globals.user.getCdg(), true, false, method, true).then(result => {
                if (result?.legacyConversionRequired) {
                    localStorage.setItem('tsSessionConversionReady', 'active');
                    localStorage.setItem('tsLegacyConversion', 'selected');
                    dispatch(true);
                    return;
                }
                    finish();
                    if (securityTokenIsValid().IsValid) dfd.resolve(captureProof(), result);
                    else dfd.reject(authenticationError({ status: 401 }));
            }).fail(error => { finish(); if (error?.authenticationFailed) credentialDenied = true; dfd.reject(error); });
        } else {
            const options = utils.setOptions('authentication/refresh', 'POST', 'json', xhr => {
                xhr.setRequestHeader('X-TruckScience-Intent', 'cookie-refresh');
            }, globals.cloudServicesVersion4, true, 10000);
            options.xhrFields = { withCredentials: true };
            const request = $.ajax(options);
            pendingRequests.add(request);
            request.then((data, textStatus, xhr) => {
                pendingRequests.delete(request); finish();
                if (!isCurrent(proof)) return dfd.reject(cancelled());
            try { if (pendingPurpose(data, xhr)) return dfd.reject(authenticationError({ status: 401 })); }
            catch { return dfd.reject(authenticationError({ status: 401 })); }
                const user = data && data.User;
                const storage = xhr.getResponseHeader('LocalStorageKey');
                const machine = xhr.getResponseHeader('MachineCode');
                const token = xhr.getResponseHeader('SecurityToken');
                if (!user || !token || !token.startsWith('tsc1.') || !storage ||
                    (credentialContext && (credentialContext.instance !== user.UserLicenceInstanceId ||
                        credentialContext.storage !== storage || credentialContext.machine !== machine))) {
                    credentialDenied = true;
                    return dfd.reject(authenticationError({ status: 401 }));
                }
                if (!adoptResponse(xhr, proof)) return dfd.reject(cancelled());
                credentialRevision++;
                credentialContext = { instance: user.UserLicenceInstanceId, storage, machine, username: user.Username };
                machineCodeValue = machine;
                // Reload may acquire context; background renewal never changes model/storage.
                dfd.resolve(captureProof());
            }).fail((xhr, textStatus) => {
                pendingRequests.delete(request); finish();
                const error = authenticationError(xhr, textStatus);
                const current = isCurrent(proof);
                if (current && error.authenticationFailed) {
                    lastRenewalDenial = { failure: error.recoveryFailure, authority: proof.authority };
                    credentialDenied = true; securityTokenValue = ''; validUntilValue = ''; credentialRevision++; authorityRevision++;
                }
                dfd.reject(current ? error : cancelled());
            });
        }
    };
    if (protocolMode === 'cookie-v1' || localStorage.getItem('tsSessionConversionReady') === 'active') dispatch(true);
    else {
        // Positive authoritative readiness is checked before choosing a reader.
        // A failed conversion never chooses the compatibility path.
        const options = utils.setOptions('authentication/sessionphase', 'GET', 'json', xhr => {
            xhr.setRequestHeader('X-TruckScience-Intent', 'session-phase');
        }, globals.cloudServicesVersion4, true, 10000);
        options.xhrFields = { withCredentials: false };
        options.cache = false;
        const request = $.ajax(options);
        pendingRequests.add(request);
        request.then(data => {
            pendingRequests.delete(request);
            if (!isCurrent(proof)) { finish(); return dfd.reject(cancelled()); }
            if (!data || typeof data.LegacyConversionReady !== 'boolean') { finish(); return dfd.reject(authenticationError({ status: 401 })); }
            if (data.LegacyConversionReady) {
                localStorage.setItem('tsSessionConversionReady', 'active');
                localStorage.setItem('tsLegacyConversion', 'selected');
            }
            dispatch(data.LegacyConversionReady);
        }).fail((xhr, textStatus) => {
            pendingRequests.delete(request); finish();
            const error = authenticationError(xhr, textStatus);
            if (isCurrent(proof) && error.authenticationFailed) credentialDenied = true;
            dfd.reject(isCurrent(proof) ? error : cancelled());
        });
    }
    return renewalPromise;
}

// Exactly one dispatch, with proof captured after freshness. No write replay.
function protectedAjax(options, outsideAuthentication = false, passwordChange = false, launchBootstrap = false) {
    const dfd = $.Deferred();
    const owner = incarnation;
    let responseProof = null, dispatched = false;
    const rejected = error => dfd.reject({ ...error, protectedDispatched: dispatched });
    const release = security.registerCancellation(() => rejected(cancelled()));
    dfd.always(release);
    const ready = outsideAuthentication ? $.Deferred().resolve(captureProof()).promise() : ensureFresh();
    ready.then(proof => {
        if (!sameAuthority(proof)) return rejected(cancelled());
        // A joined renewal may have advanced credentials before this caller
        // dispatches. Capture current headers without replacing its operation.
        proof = captureProof();
        const beforeSend = options.beforeSend;
        const capturedOptions = { ...options, beforeSend(xhr) {
            if (!isCurrent(proof)) return false;
            if (beforeSend) beforeSend(xhr);
            if (!outsideAuthentication) xhr.setRequestHeader('SecurityToken', proof.token);
        } };
        dispatched = true;
        const request = $.ajax(capturedOptions);
        pendingRequests.add(request);
        request.then((data, textStatus, xhr) => {
            pendingRequests.delete(request);
            if (!sameAuthority(proof)) return rejected(cancelled());
            try { if (pendingPurpose(data, xhr)) return rejected(authenticationError({ status: 401 })); }
            catch { return rejected(authenticationError({ status: 401 })); }
            const successfulLaunch = launchBootstrap && data?.Result?.ReturnCode === 1 && !!data.EmailAddress;
            const launchStorage = successfulLaunch ? xhr.getResponseHeader('LocalStorageKey') : null;
            if (successfulLaunch && (!launchStorage || !xhr.getResponseHeader('SecurityToken') || !xhr.getResponseHeader('ValidUntil'))) {
                return rejected(authenticationError({ status: 401 }));
            }
            // Denied ticket responses never adopt credential headers. Successful
            // bootstrap retains the cookie's storage context before token-only
            // continuation, which cannot issue a new storage key or rolling cookie.
            if ((!launchBootstrap || successfulLaunch) && isCurrent(proof)) {
                if (!adoptResponse(xhr, proof, passwordChange && data && data.ReturnCode > 0)) return rejected(cancelled());
            } else if (passwordChange && data?.ReturnCode > 0) {
                // The password commit has newer authority than an overlapping
                // ordinary renewal; adopt its continuation and fence old work.
                if (!adoptResponse(xhr, captureProof(), true)) return rejected(cancelled());
            }
            if (successfulLaunch) {
                localStorageKey = launchStorage;
                machineCodeValue = xhr.getResponseHeader('MachineCode') || '';
                credentialContext = { storage: launchStorage, machine: machineCodeValue, username: data.EmailAddress };
            }
            responseProof = captureProof();
            dfd.resolve(data, textStatus, xhr);
        }).fail((xhr, textStatus) => {
            pendingRequests.delete(request);
            if (!sameAuthority(proof)) return rejected(cancelled());
            const error = authenticationError(xhr, textStatus);
            error.protectedDispatched = true;
            // Protected 401 includes access expiry. Only the cookie acquisition
            // can establish that the remembered credential itself is denied.
            if (!outsideAuthentication && error.authenticationFailed && isCurrent(proof)) { securityTokenValue = ''; validUntilValue = ''; credentialRevision++; }
            rejected(error);
        });
    }).fail(rejected);
    return guardedPromise(dfd.promise(), owner, () => responseProof);
}

function revokeCurrent() {
    const dfd = $.Deferred();
    const token = securityTokenIsValid().IsValid ? securityTokenValue : '';
    invalidatePending();
    signingOut = true;
    localStorage.setItem(signOutMarker, '1');
    if (!token || navigator.onLine === false) return dfd.resolve({ confirmed: false }).promise();
    const options = utils.setOptions('sessions/revokecurrent', 'POST', 'json', xhr => {
        xhr.setRequestHeader('SecurityToken', token);
    }, globals.cloudServicesVersion1, true, 5000);
    options.xhrFields = { withCredentials: true };
    const owner = incarnation;
    $.ajax(options).then((data, textStatus, xhr) => {
        if (owner !== incarnation) return dfd.reject(cancelled());
        dfd.resolve({ confirmed: xhr.status === 204 });
    }).fail(() => {
        if (owner !== incarnation) return dfd.reject(cancelled());
        dfd.resolve({ confirmed: false });
    });
    return dfd.promise();
}

function login(username, password, machineCode, cdg, async, isFromSignUp, authenticationMethod, backgroundLegacy = false, contextProof = null, signupStart = null) {

    //'#########################################################################################
    //' RETURN CODES
    //' 1     The user authenticated successfully
    //' -1    The username was not found
    //' -2    The password is invalid
    //' -3    The account is in arrears
    //' -4    The trial licence has expired
    //' -5    The account has been cancelled
    //' -6    The maximum number of machine code resets has been exceeded
    //' -7    Billable Items not found
    //' -8    Associated Countries not found
    //' -9    Customer Distribution Group not found
    //' -10   An unexpected error has occurred
    //'#########################################################################################

    // var dfd = $.Deferred();
    if (signupState.resume() && transferState.resume()) return $.Deferred().reject(authenticationError({ status: 401 })).promise();
    if (signupStart && transferState.resume()) return $.Deferred().reject({ cancelled: true, restrictedBusy: true }).promise();
    if (!backgroundLegacy && !contextProof) {
        invalidatePending();
        signingOut = false;
        credentialDenied = false;
        localStorage.removeItem(signOutMarker);
        localStorage.removeItem('tsSignOutStatus');
    }
    const loginDfd = $.Deferred();
    transferState.begin();
    signupState.begin();
    const loginProof = captureProof();
    let adoptedProof = null;
    globals.loginDfd = loginDfd;

    userNameProvided = username;
    passwordProvided = password;
    if(!contextProof && (authenticationMethod === config.AUTHENTICATION_METHOD.MICROSOFT || authenticationMethod === config.AUTHENTICATION_METHOD.GOOGLE)) {
        globals.user.setThirdPartyLoggedInUserTokenAsPassword(password);
    }
    machineCodeProvided = machineCode === '' ? undefined : machineCode;
    cdgProvided = cdg;
    isFromSignUpProvided = isFromSignUp !== undefined ? isFromSignUp.toString().toUpperCase() : 'FALSE';
    authenticationMethodToUse = authenticationMethod !== undefined ? authenticationMethod : config.AUTHENTICATION_METHOD.TRUCKSCIENCE;
    TScMessenger.writeDebugMessage("Security.js, login");

    const method = contextProof ? config.AUTHENTICATION_METHOD.TRUCKSCIENCE_API : authenticationMethodToUse;
    const ordinaryContext = !!contextProof && authenticationMethodToUse !== config.AUTHENTICATION_METHOD.TRUCKSCIENCE_API;
    const contextMetadata = contextProof ? { ...credentialContext } : null;
    const compatibilityIntent = backgroundLegacy && !contextProof && !username && !password &&
        [config.AUTHENTICATION_METHOD.TRUCKSCIENCE, config.AUTHENTICATION_METHOD.TRUCKSCIENCE_API, config.AUTHENTICATION_METHOD.SHARE].includes(method);
    const capturedMachine = contextProof ? contextMetadata.machine : machineCodeValue;
    var options = utils.setOptions('authentication/application', 'GET', '', xhr => {
        // Public transport version; unrelated to StructureVersion or authority.
        xhr.setRequestHeader('X-TruckScience-Client', 'ordinary-v1');
        if (!ordinaryContext) {
            xhr.setRequestHeader('ChallengeProtocol', '1');
            xhr.setRequestHeader('ChallengeFlow', transferState.flow());
        }
        xhr.setRequestHeader('LocalStorageKey', (ordinaryContext ? contextMetadata.storage : localStorageKey) || '');
        xhr.setRequestHeader('UserName', (ordinaryContext ? contextMetadata.username : username) || '');
        xhr.setRequestHeader('Password', ordinaryContext ? '' : (password || ''));
        xhr.setRequestHeader('ApplicationType', 'HTML5');
        xhr.setRequestHeader('MachineCode', capturedMachine || machineCode || '');
        xhr.setRequestHeader('CDG', ordinaryContext ? '0' : (cdg || ''));
        xhr.setRequestHeader('IsFromSignUp', ordinaryContext ? 'FALSE' : isFromSignUpProvided);
        xhr.setRequestHeader('AuthenticationMethod', method);
        if (ordinaryContext) xhr.setRequestHeader('X-TruckScience-Intent', 'session-context');
        if (compatibilityIntent) xhr.setRequestHeader('X-TruckScience-Intent', 'legacy-context');
        if (method === config.AUTHENTICATION_METHOD.TRUCKSCIENCE_API) {
            if (!compatibilityIntent) xhr.setRequestHeader('SecurityToken', contextProof ? contextProof.token : loginProof.token);
            xhr.setRequestHeader('Embedded', window.self !== window.top);
        }
    }, globals.cloudServicesVersion4, true);
    // if(authenticationMethod !== config.AUTHENTICATION_METHOD.TRUCKSCIENCE_API) {
        options.xhrFields = {
            withCredentials: true
       }
    // }


    var curOp = 'login';
    var curTimeOut = options.timeout;


    // make the ajax call
    let transferShown = false, transferBusy = false, transferFinished = false;
    let signupShown = false, signupBusy = false, signupFinished = false, signupUpdate = null;
    const signupListeners = new Set();
    let transferUpdate = null;
    const transferListeners = new Set();
    function cancelSignupForSessionChange() {
        if (signupFinished || !(signupStart || signupShown || resumedSignup)) return false;
        signupFinished = true; signupBusy = false;
        const previous = signupUpdate?.pendingSignup || resumedSignup;
        const current = signupState.resume();
        if (previous && current?.Proof === previous.Proof && current.ExpiresUtc === previous.ExpiresUtc) signupState.clear();
        const error = { ...cancelled(), signupCancelled: true, sessionChanged: true };
        publishSignup(previous, error);
        loginDfd.reject(error);
        return true;
    }
    function signupProofIsCurrent() {
        if (isCurrent(loginProof)) return true;
        cancelSignupForSessionChange();
        return false;
    }
    function sessionCancelled() {
        if (cancelSignupForSessionChange()) return;
        if (transferFinished) return;
        transferFinished = true;
        const previous = transferUpdate?.pendingTransfer || resumedTransfer;
        if (previous && transferState.resume()?.Proof === previous.Proof) transferState.restart();
        const error = { ...cancelled(), transferCancelled: true, sessionChanged: true };
        publishTransfer(previous, error);
        loginDfd.reject(error);
    }
    cancellationHandlers.add(sessionCancelled);
    loginDfd.always(() => cancellationHandlers.delete(sessionCancelled));
    const resumedTransfer = transferState.resume();
    const resumedSignup = signupState.resume();
    if (signupStart) cancelStalePublicSignup = signupProofIsCurrent;
    let loginRequest;
    if (resumedSignup) {
        loginRequest = $.Deferred();
        setTimeout(() => {
            showSignup(resumedSignup);
            if (signupState.active()) signupOperation('status');
        }, 0);
    } else if (resumedTransfer) {
        loginRequest = $.Deferred();
        setTimeout(() => {
            showTransfer(resumedTransfer);
            if (resumedTransfer.State === 'pending' || resumedTransfer.State === 'throttled') transferOperation('status');
        }, 0);
    } else loginRequest = signupStart ? signupTransport(signupStart) : $.ajax(options);
    if (!resumedTransfer && !resumedSignup) pendingRequests.add(loginRequest);
    loginRequest
        .then(requestSucceeded)
        .fail(requestFailed);

    // handle the ajax callback
    function requestSucceeded(data, textStatus, jqXHR, fromSignupVerify = false) {
        pendingRequests.delete(loginRequest);
        if (!signupProofIsCurrent()) return loginDfd.reject(cancelled());
        if (compatibilityIntent && data?.LegacyConversionRequired !== undefined) {
            if (data.LegacyConversionRequired === true && !data.User && !data.Result && !jqXHR.getResponseHeader('SecurityToken') && jqXHR.getResponseHeader('X-TruckScience-Session-Phase') === 'conversion-required') {
                return loginDfd.resolve({ legacyConversionRequired: true });
            }
            return loginDfd.reject(authenticationError({ status: 401 }));
        }
        // A public quality denial grants no authentication or pending authority.
        if (signupStart && !signupShown && data?.Result?.ReturnCode === -7 && !data.User && !data.PendingSignup && !data.PendingTransfer && !ordinaryHeaders.some(h => jqXHR.getResponseHeader(h))) {
            return loginDfd.resolve({ ReturnCode: -7 });
        }
        let purpose;
        try { purpose = pendingPurpose(data, jqXHR); }
        catch { return loginDfd.reject(authenticationError({ status: 401 })); }
        if (purpose) {
            try {
                if (signupStart && !signupShown && purpose !== 'signup') throw new Error('Wrong signup start purpose');
                if (purpose === 'signup') {
                    if (transferShown || signupFinished) throw new Error('Wrong purpose');
                    showSignup(signupState.accept(data.PendingSignup));
                } else {
                    if (signupShown && !fromSignupVerify) throw new Error('Wrong purpose');
                    const pending = transferState.accept(data.PendingTransfer);
                    if (signupShown) { signupState.clear(); signupFinished = true; publishSignup(null, null, { completed: true, switchedPurpose: true }); }
                    showTransfer(pending);
                }
            } catch { return loginDfd.reject(authenticationError({ status: 401 })); }
            return;
        }
        if (signupStart && !signupShown) return loginDfd.reject(authenticationError({ status: 401 }));
        if (signupShown && !signupFinished) {
            if (!signupBusy || !fromSignupVerify) return loginDfd.reject(authenticationError({ status: 401 }));
            if (data?.Result?.ReturnCode === 1 && !data.User && !ordinaryHeaders.some(h => jqXHR.getResponseHeader(h))) {
                signupState.clear(); signupFinished = true;
                publishSignup(null, null, { completed: true, signInRequired: true });
                adoptedProof = captureProof();
                return loginDfd.resolve({ signupVerified: true, signInRequired: true });
            }
        }
        if (!data?.Result || ((data.Result.ReturnCode > 0 || data.Result.ReturnCode === -4 || data.Result.ReturnCode === -5) &&
            (!data.User || !(signupShown || transferShown ? ordinaryHeaders : ['SecurityToken', 'ValidUntil']).every(h => jqXHR.getResponseHeader(h))))) return loginDfd.reject(authenticationError({ status: 401 }));
        if ((signupShown || transferShown) && !jqXHR.getResponseHeader('SecurityToken')?.startsWith('tsc1.')) return loginDfd.reject(authenticationError({ status: 401 }));
        if (signupShown && !signupFinished) { signupState.clear(); signupFinished = true; publishSignup(null, null, { completed: true }); }
        if (transferShown && (!transferBusy || transferFinished)) return;
        if (transferShown) { transferFinished = true; transferState.clear(); }
        var returnedCultureCode, returnedCode, trialDays, defaultCdg, defaultApplication,
            defaultVehicleType, fullDays, versions, reportGraphics, defaultApplicationCode,
            defaultVehicleTypeCode, appOptionItemObjects = [];

        if (data.Result.ReturnCode > 0 || data.Result.ReturnCode === -4 || data.Result.ReturnCode === -5) {
            securityTokenValue = jqXHR.getResponseHeader('SecurityToken');
            validUntilValue = jqXHR.getResponseHeader('ValidUntil');
            machineCodeValue = jqXHR.getResponseHeader('MachineCode') || capturedMachine || '';
            localStorageKey = jqXHR.getResponseHeader('LocalStorageKey') || (credentialContext && credentialContext.storage) || localStorageKey;
            if (!securityTokenValue || !validUntilValue) return loginDfd.reject(authenticationError({ status: 401 }));
            rememberProtocol(securityTokenValue);
            credentialRevision++;
            credentialContext = { instance: data.User.UserLicenceInstanceId, storage: localStorageKey, machine: machineCodeValue, username: data.User.Username };
        }

        globals.setPreventReAuthAttempt(false);

        if (data.Result.ReturnCode > 0 || data.Result.ReturnCode === -4 || data.Result.ReturnCode === -5) {

            var palette = { colour1: data.User.Palette.Colour1, colour2: data.User.Palette.Colour2, colour3: data.User.Palette.Colour3, colour4: data.User.Palette.Colour4,
                colourNavBarBackground1: data.User.Palette.ColourNavBarBackground1, colourNavBarBackground2: data.User.Palette.ColourNavBarBackground2,
                colourTabBackground: data.User.Palette.ColourTabBackground, colourReminderLate: data.User.Palette.ColourReminderLate, colourReminderPresent: data.User.Palette.ColourReminderPresent,
                colourReminderFuture: data.User.Palette.ColourReminderFuture, colourTooltip: data.User.Palette.ColourTooltip, colourButtonDark: data.User.Palette.ColourButtonDark,
                colourButtonLight: data.User.Palette.ColourButtonLight, colourButtonBorder: data.User.Palette.ColourButtonBorder, colourButtonFont: data.User.Palette.ColourButtonFont,
                colourVehicleTypeManufacturer: data.User.Palette.ColourVehicleTypeManufacturer, colourVehicleTypeGeneric: data.User.Palette.ColourVehicleTypeGeneric,
                colourWizardBreadcrumbsActive: data.User.Palette.ColourWizardBreadcrumbsActive, colourWizardActive: data.User.Palette.ColourWizardActive,
                colourBorder1: data.User.Palette.ColourBorder1, colourBorder2: data.User.Palette.ColourBorder2, colourBoxShadow1: data.User.Palette.ColourBoxShadow1,
                colourBorderMenu: data.User.Palette.ColourBorderMenu, colourFontDark: data.User.Palette.ColourFontDark, colourFontLight: data.User.Palette.ColourFontLight,
                colourFontOther1: data.User.Palette.ColourFontOther1, colourFontOther2: data.User.Palette.ColourFontOther2, colourFontOther3: data.User.Palette.ColourFontOther3,
                colourToastrInfo: data.User.Palette.ColourToastrInfo
            };

            reportGraphics = {
                reportHeaderURL: data.User.ReportGraphics.ReportHeaderURL,
                reportFooterURL: data.User.ReportGraphics.ReportFooterURL,
                landscapeReportHeaderLeftURL: data.User.ReportGraphics.LandscapeReportHeaderLeftURL
            };
            if (data.User.UserType.indexOf('Trial') !== -1) {
                reportGraphics.landscapeReportHeaderLeftURL = config.getTrialUserReportLogoURL();
            }
            var decimalPlaces = {};
            if(data.User.DecimalPlaces != undefined) {
                decimalPlaces = {
                    decimalsHighPricedItems: data.User.DecimalPlaces.DecimalsHighPricedItems ? data.User.DecimalPlaces.DecimalsHighPricedItems : 0,
                    decimalsMediumPricedItems: data.User.DecimalPlaces.DecimalsMediumPricedItems ? data.User.DecimalPlaces.DecimalsMediumPricedItems : 0,
                    decimalsLowPricedItems: data.User.DecimalPlaces.DecimalsLowPricedItems ? data.User.DecimalPlaces.DecimalsLowPricedItems : 0
                };
            } else {
                decimalPlaces = {
                    decimalsHighPricedItems: 0,
                    decimalsMediumPricedItems: 0,
                    decimalsLowPricedItems: 0
                };
            }
            var reminderDate = data.User.Reminders;

            defaultCdg = data.User.CustomerDistributionGroups.filter(function (cdgElement) {
                return cdgElement.Default;
            });

            defaultApplication = data.User.AccessibleApplications.filter(function (application) {
                return application.Default;
            });

            defaultVehicleType = data.User.AccessibleVehicleTypes.filter(function (vehicleType) {
                return vehicleType.Default;
            });

            defaultApplicationCode = defaultApplication.length > 0 ? defaultApplication[0].Code : '';
            defaultVehicleTypeCode = defaultVehicleType.length > 0 ? defaultVehicleType[0].Code : '';

            if (data.User === null) {
                fullDays = 0;
                trialDays = 0;
                versions = null;
            } else {
                trialDays = data.User.TrialDays;
                fullDays = data.User.FullDays;
                versions = data.Versions;
            }

            var validUntilOffline, validUntilOfflineMillis, date;
            if (trialDays > 0) {
                validUntilOfflineMillis = Date.now() + (trialDays * config.millisInDay);
                date = new Date(validUntilOfflineMillis);
                validUntilOffline = moment(date).format("M/D/YYYY h:mm:ss a"); // User Story 17565
            } else {
                validUntilOfflineMillis = Date.now() + (fullDays * config.millisInDay);
                date = new Date(validUntilOfflineMillis);
                validUntilOffline = moment(date).format("M/D/YYYY h:mm:ss a"); // User Story 17565
            }

            globals.machineCodeFlag.value = data.User.MachineCodeFlag || 'NOT_SET'; // User Story 17565

            data.User.AppOptionItems.forEach(function (appOptionItemString) {
                appOptionItemObjects.push(JSON.parse(appOptionItemString));
            });

            globals.setStorageKey(localStorageKey);
            globals.user.getMostRecentUser();

            // if(data.User.AppMessengerMode && (data.User.AppMessengerMode === 'DEBUG' || data.User.AppMessengerMode === 'TIMER')) {
            //     if(data.User.AppMessengerMode === 'DEBUG') {
            //         TScMessenger.setMessengerModeToDebug();
            //     } else if(data.User.AppMessengerMode === 'TIMER') {
            //         TScMessenger.setMessengerModeToTimer();
            //     }
            //     TScMessenger.setIntercomCallback(globals.getSendEventInfoToIntercomCallback());
            // }
            utils.handleAppMessengerModeOverride(data.User.AppMessengerMode);

            let reportSignaturesArray;
            if(data.User.ReportPdfSignatures && typeof data.User.ReportPdfSignatures === 'string' && data.User.ReportPdfSignatures !== '') {
                try {
                    reportSignaturesArray = JSON.parse(data.User.ReportPdfSignatures);
                    if(typeof reportSignaturesArray === 'string') {
                        reportSignaturesArray = JSON.parse(reportSignaturesArray);
                    }
                } catch (error) {
                    reportSignaturesArray = [data.User.ReportPdfSignatures];
                }
            } else {
                reportSignaturesArray = [];
            }
            
            

            TScMessenger.writeDebugMessage("Security.js, requestSucceeded, updating user");
            userContextRevision++;
            globals.user.updateUser({
                firstName: data.User.FirstName,
                lastName: data.User.LastName,
                emailAddress: data.User.EmailAddress,
                position: data.User.Position,
                directNumber: data.User.DirectNumber,
                mobileNumber: data.User.MobileNumber,
                companyNumber: data.User.CompanyNumber,
                address: data.User.Address,
                cultureCode: data.User.CultureCode,
                reminders: data.User.Reminders,
                // securityToken: securityTokenValue,
                // validUntil: validUntilValue,
                // validUntilOffline: validUntilOffline,
                // machineCode: machineCodeValue,
                username: data.User.Username,
                // password: password,
                palette: palette,
                permissions: data.User.Permissions,
                //appOptionItems: data.User.AppOptionItems,
                appOptionItems: appOptionItemObjects,
                decimalPlaces: decimalPlaces,
                reminderDate: reminderDate,
                customerDistributionGroups: data.User.CustomerDistributionGroups,
                accessibleApplications: data.User.AccessibleApplications,
                accessibleVehicleTypes: data.User.AccessibleVehicleTypes,
                cdg: defaultCdg[0].Id,
                defaultApplication: defaultApplicationCode,
                defaultVehicleType: defaultVehicleTypeCode,
                trainingUrl: data.User.TrainingUrl,
                supportEmailAddress: data.User.SupportEmailAddress,
                supportTelephoneNumber: data.User.SupportTelephoneNumber,
                supportTelephoneNumberForLink: data.User.SupportTelephoneNumberForLink,
                supportWebsite: data.User.SupportWebsite,
                countryName: data.User.CountryName,
                activeLegislationId: data.User.LegislationId,
                activeMeasurementSystemConfigurationId: data.User.MeasurementSystemId,
                lengthIncrement: data.User.LengthIncrement,
                massIncrement: data.User.MassIncrement,
                percentageIncrement: data.User.PercentageIncrement,
                reportGraphics: reportGraphics,
                colourDrawings: data.User.ColourDrawings,
                loggedInUsingEmail: data.User.LoggedInUsingEmail,
                emailVerifiedStatus: data.User.EmailVerifiedStatus,
                userType: data.User.UserType,
                trialCalculationsRemaining: data.User.TrialCalculationsRemaining,
                numberOfUsesBeforeReferralPrompt: data.User.NumberOfUsesBeforeReferralPrompt,
                numberOfUsesSinceLastPrompt: data.User.NumberOfUsesSinceLastPrompt,
                firstLogin: data.User.FirstLogin,
                trialDaysRemaining: trialDays,
                billingDetails: data.User.BillingDetails,
                intercomUserId: data.User.IntercomUserId,
                companyName: data.User.CompanyName,
                companyId: data.User.CompanyId,
                expiryDate: data.User.ExpiryDate,
                firstLoginDate: data.User.FirstLoginDate,
                latestLoginDate: data.User.LatestLoginDate,
                userPlan: data.User.UserPlan,
                authenticationMethod: authenticationMethodToUse,
                integrations: data.User.Integrations,
                defaultReportPdfPageSize: data.User.DefaultReportPdfPageSize,
                userLicenceInstanceId: data.User.UserLicenceInstanceId,
                isMemberOfDistributorCompany: data.User.IsMemberOfDistributorCompany,
                companyAdditionalNote: data.User.CompanyAdditionalNote,
                specifyBodyPositionAs: data.User.SpecifyBodyPositionAs,
                specifyEquipmentPositionAs: data.User.SpecifyEquipmentPositionAs,
                countryAbbreviation: data.User.CountryAbbreviation,
                edition: data.User.Edition,
                product: data.User.Product,
                appMessengerModeOverride: data.User.AppMessengerMode || undefined,
                reportPdfSignatures: reportSignaturesArray,
                reportPdfStamps: data.User.ReportPdfStamps || '',
                userRootFolderId: data.User.UserRootFolderId,
                companyRootFolderId: data.User.CompanyRootFolderId
            });

            globals.updateActiveCultureCode(data.User.CultureCode);
        }

        logger.log('Retrieved security token from data source', '', '', config.showDebugToast);

        //$('#applicationBody').append('<script>(function () { var w = window; var ic = w.Intercom; if (typeof ic === "function") { ic(\'reattach_activator\'); ic(\'update\', intercomSettings); } else { var d = document; var i = function () { i.c(arguments) }; i.q = []; i.c = function (args) { i.q.push(args) }; w.Intercom = i; function l() { var s = d.createElement(\'script\'); s.type = \'text/javascript\'; s.async = true; s.src = \'https://widget.intercom.io/widget/gvykpldh\'; var x = d.getElementsByTagName(\'script\')[0]; x.parentNode.insertBefore(s, x); } if (w.attachEvent) { w.attachEvent(\'onload\', l); } else { w.addEventListener(\'load\', l, false); } } })()</script>');

        if (globals.user.displayPerformance() && !globals.scriptTagWithSrcExistsInHtml('https://www.bing.com/api/maps/mapcontrol?callback=loadMap&setMkt=en-US')) {
            // $('#applicationBody').append('<script type="text/javascript" src="https://www.bing.com/api/maps/mapcontrol?callback=loadMap&setMkt=en-US" async defer></script>');
            // $('#applicationBody').append('<link rel="stylesheet" type="text/css" href="https://js.api.here.com/v3/3.2/mapsjs-ui.css" />');
            // $('#applicationBody').append('<script type="text/javascript" src="https://js.api.here.com/v3/3.2/mapsjs-core.js"></script>');
            // $('#applicationBody').append('<script type="text/javascript" src="https://js.api.here.com/v3/3.2/mapsjs-service.js"></script>');
            // $('#applicationBody').append('<script type="text/javascript" src="https://js.api.here.com/v3/3.2/mapsjs-ui.js"></script>');
            // $('#applicationBody').append('<script type="text/javascript" src="https://js.api.here.com/v3/3.2/mapsjs-mapevents.js"></script>');

            // <link rel="stylesheet" type="text/css" href="https://js.api.here.com/v3/3.2/mapsjs-ui.css" />
            // <script type="text/javascript" src="https://js.api.here.com/v3/3.2/mapsjs-core.js"></script>
            // <script type="text/javascript" src="https://js.api.here.com/v3/3.2/mapsjs-service.js"></script>
            // <script type="text/javascript" src="https://js.api.here.com/v3/3.2/mapsjs-ui.js"></script>
            // <script type="text/javascript" src="https://js.api.here.com/v3/3.2/mapsjs-mapevents.js"></script>
        }

        // $('#applicationBody').append('<link rel="stylesheet" type="text/css" href="https://js.api.here.com/v3/3.2/mapsjs-ui.css" />');
        // $('#applicationBody').append('<script type="text/javascript" src="https://js.api.here.com/v3/3.2/mapsjs-core.js"></script>');
        // $('#applicationBody').append('<script type="text/javascript" src="https://js.api.here.com/v3/3.2/mapsjs-service.js"></script>');
        // $('#applicationBody').append('<script type="text/javascript" src="https://js.api.here.com/v3/3.2/mapsjs-ui.js"></script>');
        // $('#applicationBody').append('<script type="text/javascript" src="https://js.api.here.com/v3/3.2/mapsjs-mapevents.js"></script>');
        
        adoptedProof = captureProof();
        loginDfd.resolve({
            transferCompleted: transferShown && transferFinished,
            signupCompleted: signupShown && signupFinished,
            contextEstablished: !!data.User && (data.Result.ReturnCode > 0 || data.Result.ReturnCode === -4 || data.Result.ReturnCode === -5),
            result: data.Result,
            trialDays: trialDays,
            versions: versions,
            fullDays: fullDays,
            reportGraphics: reportGraphics
        });


    }

    function requestFailed(xhr, ajaxOptions, thrownError) {
        pendingRequests.delete(loginRequest);
        if (!signupProofIsCurrent()) return loginDfd.reject(cancelled());
        let returnObj = { ...authenticationError(xhr, ajaxOptions),
            returnCode: -10,
            failedOp: curOp,
            opTimeout: curTimeOut,
            errorMessage: xhr.statusText
        };

        loginDfd.reject(returnObj);
    }

    function publishTransfer(pending, error = null, timing = true) {
        transferUpdate = { pendingTransfer: pending, error, busy: transferBusy, timing };
        for (const listener of transferListeners) listener(transferUpdate);
    }
    function publishSignup(pending, error = null, outcome = null, timing = true) {
        signupUpdate = { pendingSignup: pending, error, outcome, busy: signupBusy, timing };
        for (const listener of signupListeners) listener(signupUpdate);
    }
    function showSignup(pending) {
        if (!signupProofIsCurrent() || signupFinished) return;
        publishSignup(pending);
        if (!signupShown) {
            signupShown = true;
            const independentSession = securityTokenIsValid().IsValid;
            loginDfd.notify({ pendingSignup: pending, operate: signupOperation, independentSession,
                subscribe(listener) { signupListeners.add(listener); listener(signupUpdate); return () => signupListeners.delete(listener); } });
        }
    }
    function cancelSignup(action, serverCancelled) {
        signupState.clear(); signupFinished = true;
        const error = { cancelled: true, signupCancelled: true, action, serverCancelled };
        publishSignup(null, error); loginDfd.reject(error);
    }
    function signupOperation(action, code) {
        const result = $.Deferred();
        if (!signupProofIsCurrent() || signupFinished || !['status','verify','resend','cancel','startAgain'].includes(action)) return result.reject(cancelled()).promise();
        if (signupBusy) return result.reject({ signupBusy: true }).promise();
        if (action === 'startAgain') {
            if (signupState.active()) {
                signupOperation('cancel').always(() => { if (!signupFinished) cancelSignup(action, false); result.resolve({ cancelled: true }); });
            } else { cancelSignup(action, false); result.resolve({ cancelled: true }); }
            return result.promise();
        }
        const pending = signupState.resume();
        if (!pending || !signupState.active()) return result.reject({ signupState: 'expired' }).promise();
        if (action === 'verify' && !/^[0-9]{8}$/.test(code)) return result.reject({ signupState: 'invalid' }).promise();
        signupBusy = true;
        // Reuse the existing incarnation-owned completion hold through admitted
        // signup bootstrap/navigation. A purpose switch or sign-in-only result
        // releases this attempt; neither is an admitted application session.
        const loading = action === 'verify' ? globals.beginTransferLoading(loginProof.incarnation, security.registerCancellation) : null;
        let handedOff = false;
        result.always(() => { if (!handedOff) globals.finishTransferLoading(loading); });
        if (loading) loginDfd.fail(() => globals.finishTransferLoading(loading));
        publishSignup(pending, null, null, false);
        const opts = utils.setOptions('authentication/signup' + action, 'POST', 'json',
            xhr => xhr.setRequestHeader('SignupChallenge', pending.Proof), globals.cloudServicesVersion4, true);
        opts.data = action === 'verify' ? JSON.stringify({ Code: code }) : '';
        const operation = signupTransport(opts);
        pendingRequests.add(operation);
        operation.then((data, text, xhr) => {
            pendingRequests.delete(operation);
            if (!signupProofIsCurrent() || signupFinished) { signupBusy = false; return result.reject(cancelled()); }
            if (data?.PendingSignup || (action === 'verify' && (data?.PendingTransfer || data?.Result?.ReturnCode === 1 ||
                (data?.User && [2, -4, -5].includes(data?.Result?.ReturnCode))))) {
                requestSucceeded(data, text, xhr, action === 'verify');
                if (loginDfd.state() === 'rejected') result.reject(authenticationError({ status: 401 }));
                else if (signupFinished) {
                    handedOff = loginDfd.state() === 'resolved' && !!data.User && data.Result.ReturnCode > 0;
                    result.resolve(signupUpdate?.outcome || { completed: true });
                }
                else if (action === 'cancel' && data.PendingSignup?.State === 'cancelled') { cancelSignup(action, true); result.resolve({ cancelled: true }); }
                else result.resolve({ pendingSignup: signupState.resume() });
            } else { publishSignup(signupState.resume(), { signupState: 'stale' }); result.reject(authenticationError({ status: 401 })); }
            signupBusy = false;
            if (!signupFinished) publishSignup(signupState.resume(), signupUpdate?.error, null, false);
        }).fail((xhr, text) => {
            pendingRequests.delete(operation); signupBusy = false;
            if (!signupProofIsCurrent() || signupFinished) return result.reject(cancelled());
            const error = { ...authenticationError(xhr, text), signupState: xhr.status === 401 ? 'stale' : 'offline' };
            publishSignup(signupState.resume(), error); result.reject(error);
        });
        return result.promise();
    }
    function cancelTransfer(action, serverCancelled) {
        transferState.restart(); transferFinished = true;
        loginDfd.reject({ cancelled: true, transferCancelled: true, action, serverCancelled });
    }
    function showTransfer(pending) {
        if (!isCurrent(loginProof) || transferFinished) return;
        publishTransfer(pending);
        if (!transferShown) {
            const independentSession = securityTokenIsValid().IsValid;
            if (!independentSession) { securityTokenValue = ''; validUntilValue = ''; credentialContext = null; }
            transferShown = true;
            loginDfd.notify({ pendingTransfer: pending, operate: transferOperation, independentSession, emailAddress: username || '',
                subscribe(listener) { transferListeners.add(listener); listener(transferUpdate); return () => transferListeners.delete(listener); } });
        }
    }
    function transferOperation(action, code) {
        const result = $.Deferred();
        if (!isCurrent(loginProof) || transferFinished || !['status','verify','resend','cancel','startAgain'].includes(action)) return result.reject(cancelled()).promise();
        if (transferBusy) return result.reject({ transferBusy: true }).promise();
        if (action === 'startAgain') {
            if (transferState.active()) {
                transferOperation('cancel').always(() => { if (!transferFinished) cancelTransfer(action, false); result.resolve({ cancelled: true }); });
                return result.promise();
            }
            cancelTransfer(action, false);
            return result.resolve({ cancelled: true }).promise();
        }
        const pending = transferState.resume();
        if (!pending || !transferState.active()) return result.reject({ transferState: 'expired' }).promise();
        transferBusy = true;
        // Confirmation belongs to the same incarnation as its successful login.
        // Keep this exact hold for bootstrap/Selection; credential adoption is
        // expected here and must not be fenced by a generic session operation.
        const loading = action === 'verify' ? globals.beginTransferLoading(loginProof.incarnation, security.registerCancellation) : null;
        let handedOff = false;
        result.always(() => { if (!handedOff) globals.finishTransferLoading(loading); });
        if (loading) loginDfd.fail(() => globals.finishTransferLoading(loading));
        publishTransfer(pending, null, false);
        const opts = utils.setOptions('authentication/transfer' + action, 'POST', 'json',
            xhr => xhr.setRequestHeader('TransferChallenge', pending.Proof), globals.cloudServicesVersion4, true);
        opts.data = action === 'verify' ? JSON.stringify({ Code: code }) : '';
        opts.contentType = 'application/json';
        opts.xhrFields = { withCredentials: true };
        const operationRequest = $.ajax(opts);
        pendingRequests.add(operationRequest);
        operationRequest.then((data, text, xhr) => {
            pendingRequests.delete(operationRequest);
            if (!isCurrent(loginProof)) { transferBusy = false; return result.reject(cancelled()); }
            if (data?.PendingTransfer) {
                try {
                    requestSucceeded(data, text, xhr);
                    if (action === 'cancel' && data.PendingTransfer.State === 'cancelled') { cancelTransfer(action, true); result.resolve({ cancelled: true, serverCancelled: true }); }
                    else result.resolve({ pendingTransfer: transferState.resume() });
                } catch { result.reject(authenticationError({ status: 401 })); }
            } else if (action === 'verify' && data?.User && data?.Result?.ReturnCode !== 3) {
                requestSucceeded(data, text, xhr);
                handedOff = loginDfd.state() === 'resolved' && data.Result.ReturnCode > 0;
                result.resolve({ completed: true });
            } else result.reject(authenticationError({ status: 401 }));
            transferBusy = false;
            if (!transferFinished) publishTransfer(transferState.resume(), transferUpdate?.error, false);
        }).fail((xhr, text) => {
            pendingRequests.delete(operationRequest); transferBusy = false;
            if (!isCurrent(loginProof) || transferFinished) return result.reject(cancelled());
            const error = { ...authenticationError(xhr, text), transferState: xhr.status === 401 ? 'stale' : 'offline' };
            publishTransfer(transferState.resume(), error, false);
            result.reject(error);
        });
        return result.promise();
    }

    return guardedPromise(loginDfd.promise(), loginProof.incarnation, () => adoptedProof);

}

function startSignup(request) {
    if (publicSignupRequest?.state() === 'pending') cancelStalePublicSignup?.();
    if (publicSignupRequest?.state() === 'pending') return publicSignupRequest;
    const options = utils.setOptions(request, 'GET', 'json', xhr => xhr.setRequestHeader('ChallengeProtocol', '1'), globals.cloudServicesVersion1, true);
    const operation = login('', '', '', 0, true, false, config.AUTHENTICATION_METHOD.TRUCKSCIENCE, false, null, options);
    publicSignupRequest = operation;
    operation.always(() => { if (publicSignupRequest === operation) { publicSignupRequest = null; cancelStalePublicSignup = null; } });
    return operation;
}

function forgottenPassword(email) {
    const dfd = $.Deferred(), proof = captureProof();
    const options = utils.setOptions('authentication/credentials?email=' + encodeURIComponent(String(email).trim()), 'GET', '', undefined, globals.cloudServicesVersion1);
    const request = $.ajax(options);
    pendingRequests.add(request);
    request.then(data => {
        pendingRequests.delete(request);
        if (!isCurrent(proof)) return dfd.reject(cancelled());
        if (data?.ReturnCode !== 1) return dfd.reject(authenticationError({ status: 503 }));
        dfd.resolve({ acknowledged: true });
    }).fail((xhr, text) => {
        pendingRequests.delete(request);
        dfd.reject(isCurrent(proof) ? authenticationError(xhr, text) : cancelled());
    });
    return guardedPromise(dfd.promise(), proof.incarnation, () => proof);
}

//function changePassword(oldPassword, newPassword) {

//    var dfd = $.Deferred();

//    //securityTokenValue = securityToken;

//    passwordObject = {
//        "OldPassword": oldPassword,
//        "NewPassword": newPassword
//    }

//    // make ajax call
//    $.ajax(utils.setPostOptions('users/changepassword', 'POST', 'json', passwordObject, setChangePasswordHeader, globals.cloudServicesVersion1))
//        .done(requestCompleted)
//        .fail(requestFailed);

//    // handle the ajax callback
//    function requestCompleted(data, textStatus, jqXHR) {

//        //update the local security token with the one received from the data layer
//        var securityToken, validUntil;
//        securityToken = jqXHR.getResponseHeader('SecurityToken');
//        validUntil = jqXHR.getResponseHeader('ValidUntil');
//        setSecurityToken(securityToken, validUntil);

//        dfd.resolve({ chgPwd: data });

//    };

//    function requestFailed(xhr, ajaxOptions, thrownError) {
//        dfd.reject({ errorMessage: xhr.statusText });
//    }

//    return dfd.promise();
//}

function logout(logoutFrom3rdPartyIdentityProvider) {
    transferState.restart();
    var dfd = $.Deferred();
    invalidatePending();
    credentialContext = null;
    // clear security token and valid until
    securityTokenValue = '';
    validUntilValue = '';
    const owner = incarnation;
    function finish() {
        if (owner !== incarnation) return dfd.reject(cancelled());
        if (dfd.state() !== 'pending') return;
        globals.user.clearUser();
        dfd.resolve();
    }

    // Shutdown Intercom
    if (typeof window.Intercom !== 'undefined') {
        setTimeout(function () {
            if (owner !== incarnation) return;
            try {
                window.Intercom('shutdown');
                var bootObject = {};
                bootObject[config.INTERCOM_USER.APP_ID] = config.intercomAppId;
                window.Intercom('boot', bootObject);
            } catch (error) {
                logger.log(error.errorMessage, undefined, "services/security", config.showDebugToast);
                dataManagerLogCallback("Error when trying to update Intercom to remove user when logging out", error, "security.logout", config.LOG_MESSAGE_TYPE.ERROR);
            }

        }, 1000);
    }

    if (globals.user.getAuthenticationMethod() === config.AUTHENTICATION_METHOD.GOOGLE && logoutFrom3rdPartyIdentityProvider === true && globals.isOnline.value === true) {
        // https://developers.google.com/identity/gsi/web/guides/revoke
        localStorage.removeItem('loginHint');
        setTimeout(finish, 3000);
        try { gisAuth.revoke(globals.user.getEmailAddress(), finish); }
        catch (error) { finish(); }
    } else if (globals.user.getAuthenticationMethod() === config.AUTHENTICATION_METHOD.MICROSOFT && logoutFrom3rdPartyIdentityProvider === true && globals.isOnline.value === true) {
        localStorage.removeItem('loginHint');
        globals.user.clearUser();
        dfd.resolve();
        let msalAuthContext = msalAuth.getAuthContext();
        if (msalAuthContext) {
            try { msalAuthContext.logoutRedirect({
                account: msalAuthContext.getActiveAccount(), 
                // onRedirectNavigate: (url) => {
                //     // Return false if you would like to stop navigation after local logout
                //     return false;
                // }
            }); } catch (error) { /* Local logout has completed. */ }
        }
        
    } else {
        // empty local and session storage
        globals.user.clearUser();

        dfd.resolve();
    }

    return dfd.promise();
}

function getSecurityToken() {

    // if (securityTokenValue === '') {
    //     //try to retrieve it from local storage
    //     securityTokenValue = globals.user.securityToken;
    // }

    return securityTokenValue;

}

function getMachineCode() {
    return machineCodeValue;
}

function setSecurityToken(token, validUntil, machineCode) {

    //store the values in memory for access application-wide
    if (!token && !validUntil) invalidatePending();
    securityTokenValue = token || '';
    validUntilValue = validUntil || '';
    if (token) { rememberProtocol(token); credentialRevision++; }

    //store it locally in case the user opens the app when the security details are still valid
    // globals.user.updateUser({
    //     // securityToken: securityTokenValue,
    //     // validUntil: validUntilValue,
    //     machineCode: machineCode
    // });

}

function getUserNameProvided() {
    return userNameProvided;
}

function securityTokenIsValid() {
    const deadline = moment.utc(validUntilValue, "M/D/YYYY h:mm:ss a", true);
    return { IsValid: !signingOut && !!securityTokenValue && !!validUntilValue &&
        deadline.isValid() && moment.utc().isBefore(deadline) };
}

function initialiseContext(inSession = false) {
    if (transferState.resume() || signupState.resume()) return login('', '', '', 0, true, false, config.AUTHENTICATION_METHOD.TRUCKSCIENCE);
    const dfd = $.Deferred();
    if (localStorage.getItem(signOutMarker)) return dfd.reject(authenticationError({ status: 401 })).promise();
    const method = globals.user.getAuthenticationMethod();
    if (!method || method === 'null') return dfd.reject(authenticationError({ status: 401 })).promise();
    const provider = method === config.AUTHENTICATION_METHOD.GOOGLE || method === config.AUTHENTICATION_METHOD.MICROSOFT;
    if (provider && protocolMode !== 'cookie-v1') {
        const providerProof = globals.user.getThirdPartyLoggedInUserTokenAsPassword();
        if (!providerProof) return dfd.reject(authenticationError(inSession ? { status: 401 } : {})).promise();
        // Refresh already fenced its work. A provider-backed continuation keeps
        // that operation's owner; a separate explicit sign-in still replaces it.
        return login(globals.user.getEmailAddress(), providerProof, '', globals.user.getCdg(), true, false, method, inSession);
    }
    const owner = incarnation;
    let contextProof = null;
    ensureFresh(true).then((proof, legacyContext) => {
        if (!isCurrent(proof)) return dfd.reject(cancelled());
        if (legacyContext !== undefined) {
            if (legacyContext?.contextEstablished !== true) return dfd.reject(authenticationError({ status: 401 }));
            contextProof = proof;
            return dfd.resolve(legacyContext);
        }
        // Deliberate complete context follows the positively selected reader.
        // Existing API proof binds the supplied token and does not slide it.
        login(credentialContext?.username || '', '', credentialContext?.machine || '', globals.user.getCdg(), true, false,
            method, false, proof).then(data => {
                contextProof = captureProof();
                dfd.resolve(data);
            }).fail(error => dfd.reject(error));
    }).fail(error => dfd.reject(error));
    return guardedPromise(dfd.promise(), owner, () => contextProof);
}

// Separate from full-context acquisition: only this protected POST can request
// a library change. It never issues/adopts credentials or treats a 2xx login as
// proof that the requested library was accepted. Legacy login routes stay intact.
function requestLibrary(target, previous) {
    const dfd = $.Deferred(), owner = incarnation;
    if (![target, previous].every(value => Number.isSafeInteger(value) && value > 0 && value <= 2147483647))
        return dfd.reject({ permissionDenied: true }).promise();
    ensureFresh(true).then(proof => {
        if (!isCurrent(proof)) return dfd.reject(cancelled());
        if (protocolMode !== 'cookie-v1' || !credentialContext) return dfd.reject({ libraryUnsupported: true });
        const metadata = { ...credentialContext };
        const options = utils.setOptions('authentication/library', 'POST', 'json', xhr => {
            xhr.setRequestHeader('X-TruckScience-Client', 'ordinary-v1');
            xhr.setRequestHeader('X-TruckScience-Intent', 'session-library');
            xhr.setRequestHeader('X-TruckScience-Previous-CDG', String(previous));
            xhr.setRequestHeader('CDG', String(target));
            xhr.setRequestHeader('SecurityToken', proof.token);
            xhr.setRequestHeader('ApplicationType', 'HTML5');
            xhr.setRequestHeader('AuthenticationMethod', config.AUTHENTICATION_METHOD.TRUCKSCIENCE_API);
            xhr.setRequestHeader('UserName', metadata.username);
            xhr.setRequestHeader('MachineCode', metadata.machine);
            xhr.setRequestHeader('LocalStorageKey', metadata.storage);
        }, globals.cloudServicesVersion4, true, 10000);
        options.xhrFields = { withCredentials: true };
        options.cache = false;
        const request = $.ajax(options);
        pendingRequests.add(request);
        request.then(data => {
            pendingRequests.delete(request);
            if (!sameAuthority(proof)) return dfd.reject({ ...cancelled(), protectedDispatched: true });
            if (data?.CustomerDistributionGroupId !== target) return dfd.reject({ libraryUnconfirmed: true, protectedDispatched: true });
            dfd.resolve(data);
        }).fail((xhr, textStatus) => {
            pendingRequests.delete(request);
            dfd.reject({ ...(sameAuthority(proof) ? authenticationError(xhr, textStatus) : cancelled()), protectedDispatched: true });
        });
    }).fail(error => dfd.reject(error));
    return guardedPromise(dfd.promise(), owner);
}

function setAuthorisationHeader(xhr) {
    xhr.setRequestHeader('X-TruckScience-Client', 'ordinary-v1');
    // xhr.withCredentials = true;
    xhr.setRequestHeader('UserName', userNameProvided);
    xhr.setRequestHeader('Password', passwordProvided);
    xhr.setRequestHeader('ApplicationType', 'HTML5');

    if(config.appVersionNumber === '2026.1.23' && localStorage.getItem('localStorageState') === null) {
        xhr.setRequestHeader('MachineCode', machineCodeProvided);
    } else {
        xhr.setRequestHeader('MachineCode', machineCodeValue);
    }
    xhr.setRequestHeader('CDG', cdgProvided);
    xhr.setRequestHeader('IsFromSignUp', isFromSignUpProvided);
    xhr.setRequestHeader('AuthenticationMethod', authenticationMethodToUse);
    if(authenticationMethodToUse === config.AUTHENTICATION_METHOD.TRUCKSCIENCE_API) {
        xhr.setRequestHeader('SecurityToken', security.getSecurityToken());
        let isInIframe = window.self !== window.top;
        xhr.setRequestHeader('Embedded', isInIframe);
    }
}

function setChangePasswordHeader(xhr, data) {
    xhr.setRequestHeader('SecurityToken', securityTokenValue);
}

function registerDataManagerLogCallback(callback) {
    dataManagerLogCallback = callback;
}

var security = {
    validUntilValue: validUntilValue,
    login: login,
    ensureFresh,
    initialiseContext,
    requestLibrary,
    fenceLibraryWork: () => { invalidatePending(); return incarnation; },
    // A denied refresh may show sign-in while retaining local recoverable work.
    // This suppresses remembered startup; only an explicit sign-in clears it.
    requireSignIn: () => {
        invalidatePending();
        securityTokenValue = ''; validUntilValue = ''; credentialDenied = true;
        localStorage.setItem(signOutMarker, '1');
        return incarnation;
    },
    hasPendingTransfer: () => !!transferState.resume(),
    hasPendingChallenge: () => !!(signupState.resume() || transferState.resume()),
    startSignup,
    protectedAjax,
    guardForSession: (promise, owner, proof) => guardedPromise(promise, owner === undefined ? incarnation : owner, proof),
    registerCancellation: callback => { cancellationHandlers.add(callback); return () => cancellationHandlers.delete(callback); },
    captureProof,
    isCurrent,
    sameAuthority,
    isCurrentRenewalDenial,
    getIncarnation: () => incarnation,
    getUserContextRevision: () => userContextRevision,
    incarnationMatches: value => value === incarnation && !signingOut,
    beginSignIn: () => { invalidatePending(); signingOut = false; credentialDenied = false; localStorage.removeItem(signOutMarker); return incarnation; },
    rejectExternalLaunch: owner => {
        if (!security.incarnationMatches(owner)) return null;
        invalidatePending();
        securityTokenValue = '';
        validUntilValue = '';
        credentialDenied = true;
        localStorage.setItem(signOutMarker, '1');
        return incarnation;
    },
    authenticationError,
    adoptResponse,
    // Deployment/client rollout selects migration before dispatch, including
    // an already-expired old token. A failed call never selects this mode.
    selectLegacyConversion: () => { localStorage.setItem('tsLegacyConversion', 'selected'); },
    revokeCurrent,
    isRememberedSuppressed: () => !!localStorage.getItem(signOutMarker),
    forgottenPassword: forgottenPassword,
    logout: logout,
    getSecurityToken: getSecurityToken,
    setSecurityToken: setSecurityToken,
    securityTokenIsValid: securityTokenIsValid,
    getUserNameProvided: getUserNameProvided,
    //changePassword: changePassword,
    registerDataManagerLogCallback: registerDataManagerLogCallback,
    getMachineCode: getMachineCode
};


export default security;
